import axios from 'axios';
import * as Sentry from '@sentry/browser';
import { signOut, getIdToken } from '@/utils/Auth';
import { redirectToLogin } from '@/utils/Router';
import _ from 'lodash';

import store from '@/store';

const unauthorizedStatus = [401, 403];

export function configAxios() {
  axios.interceptors.request.use(
    (config) => {
      store.dispatch('appState/startLoader');

      // Automatically authorize request headers with session data
      const token = getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return Promise.resolve(config);
    },
    (error) => {
      store.dispatch('appState/stopLoader');

      return Promise.reject(error);
    },
  );

  // Add a response interceptor
  axios.interceptors.response.use(
    (response) => {
      // Any status code that lie within the range of 2xx cause this function to trigger
      store.dispatch('appState/stopLoader');

      return Promise.resolve(response);
    }, (error) => {
      // Any status codes that falls outside the range of 2xx cause this function to trigger
      store.dispatch('appState/stopLoader');

      Sentry.withScope(function(scope) {
        const URL = parseURL(error.config.url);
        const status = _.get(error, 'response.status');
        // group errors together based on their method, URL and response status
        scope.setFingerprint([error.config.method, URL, status, process.env.NODE_ENV]);  
        Sentry.setExtra('stack', error.stack);

        let request: any = {};
        if (_.get(error, 'response.config.url')) {
          request = {
            method: error.config.method,
            url: error.config.url, 
            status,
            error: error.toString(),
            response: error.response.data,
          };
        }

        if (_.get(error, 'response.config.data')) {
          request.payload = error.response.config.data;
        }

        Sentry.setContext('request', request);

        // changes the way the error is presented in sentry
        error.stack = `${error.config.method.toUpperCase()} ${ URL }`;
        error.message = error.toString();

        Sentry.captureException(error);
      });

      // Check for response for Forbidden and or Unauthorized codes to send user to login page
      if (error && error.response && unauthorizedStatus.includes(error.response.status)) {
        signOut();

        return redirectToLogin();
      }

      if (error instanceof SyntaxError) {
        error = `Unknown Syntax Error - ${error}`;
      }

      return Promise.reject(error);
    });

  return axios;
}
/**
 * Parses a url
 * Useful to identify possible dinamic parts in the urls (to group request issues together)
 * 
 * @param url { string }
 */
function parseURL(url){
  return url.split(/[/?&]/g)
    .map(replaceURLParams)
    .join('/')
    .toString();
}

/**
 * Replaces numbers with ':number' expression
 * Replaces ibans with ':IBAN' expression
 * 
 * @param part { string }
 */
function replaceURLParams(part){
  if(Number(part) > 0){
    return ':number';
  }
  const basicIbanFormat = RegExp(/^[A-Z]{2}(?:[ ]?[A-Z0-9]){18,30}$/, 'g');

  if(basicIbanFormat.test(part)){
    return ':IBAN';
  }

  return part;
}